/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-04-08 10:33:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import {
  Card,
  TextStyle,
  Button,
  ButtonGroup,
  Page,
  Layout,
  DisplayText,
  Grid,
  SkeletonDisplayText,
  SkeletonBodyText,
  SkeletonPage,
  Icon,
  Badge,
  Link,
  TextField
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { DuplicateMinor, CircleTickMajor } from '@shopify/polaris-icons';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';

import { getFullAppUrlThemeEditor } from 'src/helpers';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { trackFSEvent } from 'src/integrations/fullStory';

import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import EnableBoostCoreSSR from 'src/assets/images/recommendation/enable-boost-core-ssr.png';
import CreateBoostAppFile from 'src/assets/images/recommendation/create-boost-app-file.gif';
import AddAppBlockSSR from 'src/assets/images/recommendation/add-app-block-ssr.png';
import AddRecommendationToPage from 'src/assets/images/recommendation/add-recommendation-to-page.gif';

import { ComposedProps, State } from './placement-ssr.type';

import EnhancePlacementWarningModal from 'src/components/views/recommendation/widget/item/enhance-placement-warning-modal';

class PlacementSSR extends Component<ComposedProps, State> {
  state = {
    clickedOnImAllDone: false,
    showWarningModal: false
  };

  componentWillReceiveProps(nextProps) {
    const { checkingEnableBoostCore, isEnableBoostCore, isEnableAppBlock } = nextProps;
    const { checkingEnableBoostCore: prevCheckingEnableBoostCore } = this.props;
    const { clickedOnImAllDone } = this.state;

    if (clickedOnImAllDone && prevCheckingEnableBoostCore && !checkingEnableBoostCore) {
      // Display warning modal if missing Enable boost core OR Add widget as App block
      if (!isEnableBoostCore || !isEnableAppBlock) {
        this.setState({ showWarningModal: true, clickedOnImAllDone: false });
      } else {
        this.setState({ clickedOnImAllDone: false });
        // push to theme setup page in shopify admin
        // window.open(url, '_blank', 'noopener,noreferrer');
        this.props.handleCloseFullscreenPage();
      }
    }
  }
  render() {
    const {
      i18n,
      handleCloseFullscreenPage,
      saving,
      checkingEnableBoostCore,
      isEnableBoostCore,
      isEnableAppBlock,
      widgetId,
      isCreateFlow,
      versionOS,
      templateId
    } = this.props;
    const { showWarningModal } = this.state;

    const codeSnipped01 = `{% comment %} PLEASE DO NOT EDIT THIS FILE {% endcomment %}
{% case type %}
  {% when 'filter-product-list-turbo' %}
    <div class="boost-sd__filter-product-list"></div>
    <script>
      if (window.boostSDTaeUtils && window.boostSDTaeUtils.initCollectionFilter) 
      {window.boostSDTaeUtils.initCollectionFilter();}
    </script>
  {% when 'recommendation-turbo' %}
    <div id="boost-sd-widget-{{- widgetId -}}"></div>
{% endcase %}`;

    const codeSnipped02 = `{% render 'boost-sd-app', type: 'recommendation-turbo', widgetId: 'Replace widgetId here' %}`;

    if (versionOS === 0) {
      return (
        <SkeletonPage primaryAction fullWidth={false}>
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={5} />
              </Card>
            </Layout.Section>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={5} />
              </Card>
            </Layout.Section>
          </Layout>
        </SkeletonPage>
      );
    }

    return (
      <div className="BC-Page BC-Page__RecommendationWidgetItem">
        <FullscreenBar
          onAction={handleCloseFullscreenPage}
          backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
          title={i18n.translate(`AdminHTML.Recommendation.WidgetItem.doneTitle`)}
          actions={
            <ButtonGroup>
              <Button primary onClick={this.handleCompleteSetup} loading={saving}>
                {i18n.translate('AdminHTML.Recommendation.WidgetItem.complete')}
              </Button>
            </ButtonGroup>
          }
          progress={isCreateFlow ? 100 : -1}
        />
        <div style={{ marginTop: '2rem' }}>
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <div className="setup-content">
                    <div className="setup-content-title">
                      <DisplayText size="large">
                        {i18n.translate('AdminHTML.Recommendation.WidgetItem.placement_Label')}
                      </DisplayText>
                    </div>
                    {versionOS === 2 ? (
                      <div className="tab-content">
                        <div className="tab-content-title">
                          <DisplayText size="small">
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.Recommendation.WidgetItem.placement_Title'
                              )}
                            </TextStyle>
                          </DisplayText>
                        </div>
                        <div className="tab-content-content">
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div>
                                <TextStyle variation="strong">
                                  {i18n.translate(
                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step1_Title'
                                  )}
                                </TextStyle>
                                <span style={{ marginLeft: '0.375rem' }}>
                                  <Badge status="attention">
                                    {i18n.translate('AdminHTML.Recommendation.Common.required')}
                                  </Badge>
                                </span>
                              </div>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step1_Content',
                                  {
                                    t1: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step1_Content_t1'
                                        )}
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <div className="action">
                                {checkingEnableBoostCore ? (
                                  <SkeletonDisplayText />
                                ) : isEnableBoostCore ? (
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step1_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                ) : (
                                  <Button primary onClick={this.enableBoostScore}>
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step1_Action'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div className="BC-Iframe-Container">
                                <img
                                  className="clickable"
                                  src={EnableBoostCoreSSR}
                                  alt="enable-boost-core"
                                  style={{ width: '100%' }}
                                  onClick={() =>
                                    window.open(EnableBoostCoreSSR, '_blank', 'noopener,noreferrer')
                                  }
                                />
                              </div>
                            </Grid.Cell>
                          </Grid>
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step2_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step2_Content',
                                  {
                                    nt: (
                                      <TextStyle variation="strong">
                                        {widgetId}
                                        <span className="copy">
                                          <Button
                                            icon={DuplicateMinor}
                                            plain
                                            onClick={() => this.copyClipBoard(widgetId)}
                                          />
                                        </span>
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <div className="action">
                                {checkingEnableBoostCore ? (
                                  <SkeletonDisplayText />
                                ) : isEnableAppBlock ? (
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step2_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                ) : (
                                  <Button primary onClick={this.addAppBlock}>
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placementSSR_Store2_Step2_Action'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div className="BC-Iframe-Container">
                                <img
                                  className="clickable"
                                  src={AddAppBlockSSR}
                                  alt="add-app-block"
                                  style={{ width: '100%' }}
                                  onClick={() =>
                                    window.open(AddAppBlockSSR, '_blank', 'noopener,noreferrer')
                                  }
                                />
                              </div>
                            </Grid.Cell>
                          </Grid>
                        </div>
                      </div>
                    ) : (
                      <div className="tab-content">
                        <div className="tab-content-content">
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div>
                                <TextStyle variation="strong">
                                  {i18n.translate(
                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Title'
                                  )}
                                </TextStyle>
                                <span style={{ marginLeft: '0.375rem' }}>
                                  <Badge status="attention">
                                    {i18n.translate('AdminHTML.Recommendation.Common.required')}
                                  </Badge>
                                </span>
                              </div>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content',
                                  {
                                    t1: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content_t1'
                                        )}
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <div className="action">
                                {checkingEnableBoostCore ? (
                                  <SkeletonDisplayText />
                                ) : isEnableBoostCore && false ? (
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                ) : (
                                  <>
                                    <Button primary onClick={this.enableBoostScore}>
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Action'
                                      )}
                                    </Button>
                                    <div style={{ marginTop: '0.75rem' }}>
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02',
                                        {
                                          t1: (
                                            <TextStyle variation="strong">
                                              {i18n.translate(
                                                'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_t1'
                                              )}
                                            </TextStyle>
                                          )
                                        }
                                      )}
                                      <ul style={{ marginTop: 0, paddingLeft: '1.25rem' }}>
                                        <li>
                                          {i18n.translate(
                                            'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line01',
                                            {
                                              t1: (
                                                <TextStyle variation="strong">
                                                  {i18n.translate(
                                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line01_t1'
                                                  )}
                                                </TextStyle>
                                              ),
                                              t2: (
                                                <TextStyle variation="strong">
                                                  {i18n.translate(
                                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line01_t2'
                                                  )}
                                                </TextStyle>
                                              ),
                                              t3: (
                                                <Link onClick={this.goToThemeEditor}>
                                                  {i18n.translate(
                                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line01_t3'
                                                  )}
                                                </Link>
                                              )
                                            }
                                          )}
                                        </li>
                                        <li>
                                          {i18n.translate(
                                            'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line02',
                                            {
                                              t1: (
                                                <TextStyle variation="strong">
                                                  {i18n.translate(
                                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line02_t1'
                                                  )}
                                                </TextStyle>
                                              )
                                            }
                                          )}
                                        </li>
                                        <li>
                                          {i18n.translate(
                                            'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step1_Content02_Line03'
                                          )}
                                        </li>
                                      </ul>
                                      <div className="html-placeholder">
                                        <TextField
                                          name="templateId"
                                          id="templateId"
                                          value={templateId}
                                          disabled="disabled"
                                          suffix={
                                            <Button
                                              icon={DuplicateMinor}
                                              plain
                                              onClick={() => this.copyClipBoard(templateId)}
                                            />
                                          }
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div className="BC-Iframe-Container">
                                <img
                                  className="clickable"
                                  src={EnableBoostCoreSSR}
                                  alt="enable-boost-core"
                                  style={{ width: '100%' }}
                                  onClick={() =>
                                    window.open(EnableBoostCoreSSR, '_blank', 'noopener,noreferrer')
                                  }
                                />
                              </div>
                            </Grid.Cell>
                          </Grid>
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step2_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step2_Content',
                                  {
                                    t1: (
                                      <TextStyle variation="strong">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step2_Content_t1'
                                        )}
                                      </TextStyle>
                                    ),
                                    nt1: (
                                      <TextStyle variation="negative">
                                        {i18n.translate(
                                          'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step2_Content_nt1'
                                        )}
                                      </TextStyle>
                                    )
                                  }
                                )}
                              </p>
                              <div className="action">
                                <Button onClick={this.handleOpenEditCode}>
                                  {i18n.translate(
                                    'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step2_Action'
                                  )}
                                </Button>
                              </div>
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div className="video">
                                <img
                                  className="clickable"
                                  alt="gif-create-boost-app-file"
                                  src={CreateBoostAppFile}
                                  style={{ width: '100%', border: '0.88px solid #e1e3e5' }}
                                  onClick={() =>
                                    window.open(CreateBoostAppFile, '_blank', 'noopener,noreferrer')
                                  }
                                />
                              </div>
                            </Grid.Cell>
                          </Grid>
                          <div className="content">
                            <div className="html-placeholder">
                              <TextField
                                name="codeSnipped01"
                                id="codeSnipped01"
                                value={codeSnipped01}
                                multiline={11}
                                disabled="disabled"
                                suffix={
                                  <Button
                                    icon={DuplicateMinor}
                                    plain
                                    onClick={() => this.copyClipBoard(codeSnipped01)}
                                  />
                                }
                              />
                            </div>
                          </div>

                          <div>
                            <Grid>
                              <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                                <div className="content">
                                  <TextStyle variation="strong">
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Title'
                                    )}
                                  </TextStyle>
                                  <div>
                                    {i18n.translate(
                                      'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Content',
                                      {
                                        t1: (
                                          <TextStyle variation="strong">
                                            {i18n.translate(
                                              'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Content_t1'
                                            )}
                                          </TextStyle>
                                        )
                                      }
                                    )}
                                  </div>
                                  <table>
                                    <thead>
                                      <tr>
                                        <th>
                                          {i18n.translate(
                                            'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Table_Header1'
                                          )}
                                        </th>
                                        <th>
                                          {i18n.translate(
                                            'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Table_Header2'
                                          )}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[1, 2, 3, 4].map(i => (
                                        <tr key={i}>
                                          {[1, 2].map(j => (
                                            <td key={j}>
                                              {i18n.translate(
                                                `AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Table_Row${i}_Col${j}`
                                              )}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <div className="action">
                                    <Button onClick={this.handleOpenEditCode}>
                                      {i18n.translate(
                                        'AdminHTML.Recommendation.WidgetItem.placementSSR_Store1_Step3_Action'
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </Grid.Cell>
                              <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                                <div className="video">
                                  <img
                                    className="clickable"
                                    alt="gif-add-recommendation-to-your-page"
                                    src={AddRecommendationToPage}
                                    style={{ width: '100%', border: '0.88px solid #e1e3e5' }}
                                    onClick={() =>
                                      window.open(
                                        AddRecommendationToPage,
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }
                                  />
                                </div>
                              </Grid.Cell>
                            </Grid>
                            <div className="content">
                              <div className="html-placeholder">
                                <TextField
                                  name="widgetCode"
                                  id="widgetCode"
                                  value={codeSnipped02}
                                  disabled="disabled"
                                  suffix={
                                    <Button
                                      icon={DuplicateMinor}
                                      plain
                                      onClick={() => this.copyClipBoard(codeSnipped02)}
                                    />
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </Layout.Section>
            </Layout>

            <EnhancePlacementWarningModal
              active={showWarningModal}
              addWidgetNow={this.handleCloseModal}
              doItLater={handleCloseFullscreenPage}
            />
          </Page>
        </div>
      </div>
    );
  }

  copyClipBoard = (copyText: string) => {
    const { i18n } = this.props;
    try {
      const inputText = document.createElement('textarea');
      inputText.value = copyText;
      document.body.appendChild(inputText);
      inputText.select();

      document.execCommand('copy');
      document.body.removeChild(inputText);
      showSABToast(i18n.translate('AdminHTML.Common.Action.copied', { t: copyText }), false);
    } catch (error) {}
  };

  goToThemeEditor = () => {
    const { themeId } = this.props;
    const url = getFullAppUrlThemeEditor(themeId, '?context=apps');
    window.open(url, '_blank');
  };
  enableBoostScore = () => {
    const { embedDeepLink, themeName } = this.props;
    if (!_.isEmpty(embedDeepLink)) {
      window.open(embedDeepLink, '_blank', 'noopener,noreferrer');

      // Track Full Story event
      trackFSEvent('Enable Boost Core', {
        theme: themeName
      });
    }
  };

  addAppBlock = () => {
    const { appBlockDeepLink, themeName } = this.props;
    if (!_.isEmpty(appBlockDeepLink)) {
      window.open(appBlockDeepLink, '_blank', 'noopener,noreferrer');

      // Track Full Story event
      trackFSEvent('Add App Block', {
        theme: themeName
      });
    }
  };

  // Handle setup action: complete
  handleCompleteSetup = async () => {
    const { widgetId } = this.props;

    // Turn on checking placement process
    this.setState({ clickedOnImAllDone: true });

    // Get enable boost core on live theme with specific widget
    const queries = { widgetId };
    await this.props.checkEnableBoostCore(queries);
  };

  // Click on Add widget now (remain on the same page) from warning modal
  handleCloseModal = () => {
    this.setState({ showWarningModal: false, clickedOnImAllDone: false });
  };

  setEnableVideo = videoType => {
    this.setState({ [videoType]: true });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Check enable boost core on live theme
  checkEnableBoostCore: queries =>
    dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    saving: _.get(state.recommendation, 'saving', false),
    checkingEnableBoostCore: _.get(state.recommendation, 'checkingEnableBoostCore', false),
    isEnableBoostCore: _.get(state.recommendation, 'isEnableBoostCore', false),
    isEnableAppBlock: _.get(state.recommendation, 'isEnableAppBlock', false),
    appBlockDeepLink: _.get(state.recommendation, 'appBlockDeepLink', ''),
    embedDeepLink: _.get(state.recommendation, 'embedDeepLink', ''),
    widgetId: _.get(state.recommendation, 'widgetId', ''),
    recommendationWidget: _.get(state.recommendation, 'recommendationWidget', {}),
    themeName: _.get(state.themeSetting, 'themeName', ''),
    themeId: _.get(state.themeSetting, 'themeId', ''),
    templateId: _.get(state.themeIntegration, 'templateInstance.templateId', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PlacementSSR));
